import { Flight } from './flightList';

export interface FlightEffectiveDate {
	/**
	 * Cutoff date in ISO 8601 format (e.g. `'2026-02-09'`).
	 *
	 * Comparison is time-precise (millisecond) via `Date.getTime()`. A bare
	 * `YYYY-MM-DD` string is parsed by the platform as UTC midnight for that
	 * day; pass a full ISO timestamp (with time and offset) if you need a
	 * specific moment within the day.
	 */
	effectiveFrom: string;
	/**
	 * Documentation-only: which field on the caller's domain object provides
	 * the date passed to {@link isFlightEffectiveForItem}. The runtime value
	 * always comes from the call site - this property exists so reviewers can
	 * see, at the registry, which field is expected.
	 */
	itemDateField: string;
	/** Free-form note for reviewers - e.g. "EST GA launch". */
	description?: string;
}

/**
 * Sparse registry of flights that have a date-based effectivity gate layered
 * on top of the existing on/off flight value. Flights not present here behave
 * exactly like a regular boolean flight.
 *
 * Semantics: the flight is effective for an item only when the item's date is
 * on or after `effectiveFrom`. (If we ever need the inverse direction for a
 * sunset/grandfathering case, add an `appliesTo` discriminator at that time.)
 *
 * To add a new date gate:
 *   1. Ensure the flight already exists in `Flight` and the per-environment
 *      records in `flightList.ts`.
 *   2. Add an entry below pointing at the cutoff date.
 *   3. At the call site, replace `useIsFlightEnabled(Flight.x)` with
 *      `useIsFlightEffectiveForItem(Flight.x, item.someDate)`.
 *
 * To remove a date gate (gate is no longer needed but the flight remains),
 * delete the entry. No other code changes are required.
 */
export const flightEffectiveDates: Partial<Record<Flight, FlightEffectiveDate>> = {
	/**
	 * EST GA launch. The Subscription Management panel shows the new EST
	 * experience only when the selected asset's term started on or after the
	 * launch date; older assets continue to see the pre-EST view. The landing
	 * page (which shows a mix of old and new assets) is intentionally NOT
	 * gated by this entry - it stays on the master flight value.
	 */
	[Flight.est]: {
		effectiveFrom: '2026-02-09',
		itemDateField: 'productConfig.selectedAsset.termInfo.startDate',
		description: 'EST GA launch - assets whose term started on/after 2026-02-09 see the new view.',
	},
};

/**
 * Parses a value into a `Date`, returning `null` for anything that is not a
 * valid date. Accepts `Date`, ISO strings, or numeric timestamps. Used to
 * shield the gate from malformed inputs without throwing.
 */
const toDate = (value: Date | string | number | null | undefined): Date | null => {
	if (value === null || value === undefined || value === '') {
		return null;
	}
	const parsed = value instanceof Date ? value : new Date(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Pure evaluator for the flight-effective-date framework. The hook
 * `useIsFlightEffectiveForItem` and the selector `getIsFlightEffectiveForItem`
 * are thin wrappers around this function.
 *
 * Contract:
 *   1. `flightEnabled === false` → `false`. The flight is the master kill
 *      switch and always wins.
 *   2. `flightEnabled === true` and no metadata entry for `flight` → `true`.
 *      The framework is opt-in; flights without an entry behave as today.
 *   3. `flightEnabled === true`, entry exists, but `itemDate` is missing or
 *      invalid → `true`. The flight value takes precedence when there is no
 *      date to compare against, preserving today's behavior at call sites
 *      that haven't yet wired up a date.
 *   4. `flightEnabled === true`, entry exists, `itemDate` valid →
 *      `itemDate.getTime() >= cutoff.getTime()`.
 *
 * @param flight        The flight whose effectivity is being evaluated.
 * @param flightEnabled The current boolean value of that flight.
 * @param itemDate      The reference date on the caller's domain object
 *                      (e.g. the selected asset's `termInfo.startDate`).
 */
export const isFlightEffectiveForItem = (
	flight: Flight,
	flightEnabled: boolean,
	itemDate: Date | string | number | null | undefined
): boolean => {
	if (!flightEnabled) {
		return false;
	}

	const meta = flightEffectiveDates[flight];
	if (!meta) {
		return true;
	}

	const parsedItemDate = toDate(itemDate);
	if (!parsedItemDate) {
		return true;
	}

	const cutoff = toDate(getOverriddenEffectiveFrom(flight) ?? meta.effectiveFrom);
	if (!cutoff) {
		// Misconfigured registry entry - fail open to the flight value rather
		// than silently blocking everyone. Surfacing this loudly in dev would
		// be nicer, but the framework deliberately keeps zero runtime side
		// effects.
		return true;
	}

	return parsedItemDate.getTime() >= cutoff.getTime();
};

/**
 * Dev-only URL override (no-op in production builds). Lets the team test a
 * different cutoff date without a code change. The query param is
 * intentionally obscure so it is unlikely to be discovered by users.
 *
 * Usage: append `?_pmwTimeWarp=<flight>:<YYYY-MM-DD>` to the URL. Multiple
 * flights can be overridden with comma separation, e.g.
 * `?_pmwTimeWarp=est:2025-01-01,foo:2027-06-15`. Only the `effectiveFrom`
 * date is overridden; the kill-switch and missing-date behavior remain as
 * defined in the registry. Flights not present in `flightEffectiveDates`
 * cannot be overridden via this mechanism.
 */
const TIME_WARP_PARAM = '_pmwTimeWarp';
const isTimeWarpEnabled =
	typeof process !== 'undefined' &&
	typeof process.env !== 'undefined' &&
	process.env.NODE_ENV !== 'production';
let cachedOverrides: Partial<Record<Flight, string>> | null = null;
let cachedSearch: string | null = null;

const parseTimeWarpOverrides = (): Partial<Record<Flight, string>> => {
	if (!isTimeWarpEnabled || typeof window === 'undefined') {
		return {};
	}
	const currentSearch = window.location?.search ?? '';
	if (cachedOverrides && cachedSearch === currentSearch) {
		return cachedOverrides;
	}
	cachedSearch = currentSearch;
	const result: Partial<Record<Flight, string>> = {};
	if (currentSearch) {
		const raw = new URLSearchParams(currentSearch).get(TIME_WARP_PARAM);
		if (raw) {
			for (const entry of raw.split(',')) {
				const [name, date] = entry.split(':');
				if (name && date && (Object.values(Flight) as string[]).includes(name)) {
					result[name as Flight] = date;
				}
			}
		}
	}
	cachedOverrides = result;
	return result;
};

const getOverriddenEffectiveFrom = (flight: Flight): string | undefined =>
	parseTimeWarpOverrides()[flight];
